import React, { useState, useEffect, useRef } from 'react';
import { IconButton } from '@fluentui/react';
import { FolderRegular, ChevronDownRegular, ChevronUpRegular } from '@fluentui/react-icons';
import { AvailableDataSources, DataSource, updateUserPreferences } from '../../api/dataSourceApi';
import { useDataContext } from '../../contexts/DataContext';
import styles from '../ChatControls/ChatControls.module.css';

interface DataSourceSelectorProps {
  selectedDataSource: string | null;
  onDataSourceChange: (dataSourceId: string | null) => void;
  className?: string;
}

export const DataSourceSelector: React.FC<DataSourceSelectorProps> = ({
  selectedDataSource,
  onDataSourceChange,
  className
}) => {
  const { availableDataSources, isLoading, refreshUserPreferences } = useDataContext();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [internalSelection, setInternalSelection] = useState<string | null>(selectedDataSource);
  const [isSaving, setIsSaving] = useState(false);  // Track saving state
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  // Sync internal selection with prop changes (but not during saves)
  useEffect(() => {
    if (!isSaving) {
      setInternalSelection(selectedDataSource);
    }
  }, [selectedDataSource, isSaving]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [dropdownOpen]);

  const handleDataSourceSelection = async (dataSourceId: string | null) => {
    // Update internal state immediately for instant UI feedback
    setInternalSelection(dataSourceId);
    setIsSaving(true);  // Prevent prop overrides during save
    
    // Update parent state immediately for UI responsiveness
    onDataSourceChange(dataSourceId);
    setDropdownOpen(false);
    
    // Update user preferences - explicitly send null for All Sources
    try {
      const valueToSave = dataSourceId === null ? null : dataSourceId;
      await updateUserPreferences({ selected_data_source: valueToSave });
      
      // Refresh user preferences to ensure UI stays in sync
      await refreshUserPreferences();
    } catch (error) {
      console.error('Failed to update data source preference:', error);
      // Revert internal state on error
      setInternalSelection(selectedDataSource);
    } finally {
      setIsSaving(false);  // Re-enable prop syncing
    }
  };

  const getSelectedDataSourceLabel = (): string => {
    if (!internalSelection || !availableDataSources) {
      return 'All Sources';
    }

    // Find the selected data source across all categories
    for (const category of availableDataSources.categories) {
      const source = category.sources.find(s => s.id === internalSelection);
      if (source) {
        return source.name;
      }
    }

    return 'All Sources';
  };

  const getAllDataSources = (): DataSource[] => {
    if (!availableDataSources) return [];
    return availableDataSources.categories.flatMap(category => category.sources);
  };

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }} className={className}>
      <IconButton
        title={`Data Sources: ${getSelectedDataSourceLabel()} - All Sources: AI analyzes and chooses best sources | Specific Source: Direct fast routing to selected source`}
        onClick={() => {
          setDropdownOpen(!dropdownOpen);
        }}
        disabled={isLoading}
        className={`${styles.controlButton} ${(dropdownOpen || internalSelection) ? styles.controlButtonActive : ''}`}
        styles={{
          root: { minWidth: '40px' },
          rootHovered: { 
            background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.1) 100%)',
            borderColor: 'rgba(102, 126, 234, 0.4)',
            color: '#667eea'
          },
          rootDisabled: {
            backgroundColor: '#f8f8f8',
            border: '1px solid #e0e0e0',
            color: '#bbb'
          }
        }}
      >
        <FolderRegular style={{ fontSize: '16px' }} />
        <div className={styles.chevronIndicator}>
          {dropdownOpen ? <ChevronUpRegular /> : <ChevronDownRegular />}
        </div>
        {internalSelection && (
          <div className={styles.statusBadge}>
            ✓
          </div>
        )}
      </IconButton>
      
      {dropdownOpen && availableDataSources && (
        <div className={styles.dropdownMenu}>
          {/* All Sources Option */}
          <div
            onClick={() => handleDataSourceSelection(null)}
            className={`${styles.dropdownItem} ${!internalSelection ? styles.dropdownItemSelected : ''}`}
          >
            All Sources
          </div>

          {/* Categorized Data Sources */}
          {availableDataSources && availableDataSources.categories && availableDataSources.categories.map((category, categoryIndex) => (
            <div key={category.name}>
              {/* Category Sources */}
              {category.sources.map((source, sourceIndex) => (
                <div
                  key={source.id}
                  onClick={() => handleDataSourceSelection(source.id)}
                  className={`${styles.dropdownItem} ${internalSelection === source.id ? styles.dropdownItemSelected : ''}`}
                  title={source.description || source.name}
                >
                  {source.name}
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};